import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { UntypedFormControl } from '@angular/forms';
import { FlyoutDataType, FlyoutOpeningData, ManageTagRequest, PersonDetails, SessionDetails } from '../flyout-models';
import { Subscription } from 'rxjs';
import { FlyoutService } from '../flyout.service';
import { SnackBarService } from '../../core/snack-bar.service';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-flyout-tags',
  templateUrl: './flyout-tags.component.html',
  styleUrls: ['./flyout-tags.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutTagsComponent implements OnInit, OnDestroy {
  private readonly logger: Logger;
  private _openingData: FlyoutOpeningData;
  private personDetails: PersonDetails;
  private personSubscription: Subscription;
  private sessionDetails: SessionDetails;
  private sessionSubscription: Subscription;
  private subscriptionArray: Subscription[] = [];
  @Output() public onVisitDetailsTagsUpdated = new EventEmitter<Array<string>>();
  public isSession = false;

  public tagsVisible = true;
  public tagsSelectable = true;
  public tagsRemovable = true;
  public tagsAddOnBlur = false;
  public tagsSeparatorKeysCodes: number[] = [ENTER, COMMA];
  public tagsCtrl = new UntypedFormControl();
  public filteredTags: string[] = [];
  public selectableTags: string[];
  public tags: string[] | any = [];

  @Input()
  public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this._openingData = openingData;
    this.isSession = !this._openingData || this._openingData.type === FlyoutDataType.Session;
  }

  public get flyoutOpeningData(): FlyoutOpeningData {
    return this._openingData;
  }

  constructor(
    public readonly loggerFactory: LoggerFactory,
    private readonly flyoutService: FlyoutService,
    private readonly snackbarService: SnackBarService,
  ) {
    this.logger = loggerFactory.get('FlyoutTagsComponent');
  }

  ngOnInit() {
    this.sessionSubscription = this.flyoutService.sessionDetailsBS.subscribe((sessionDetails: SessionDetails) => {
      this.sessionDetails = sessionDetails;
    });
    this.personSubscription = this.flyoutService.personDetailsBS.subscribe((personDetails: PersonDetails) => {
      this.personDetails = personDetails;
    });
    switch (this._openingData?.type) {
      case FlyoutDataType.Session:
        this.tags = this.sessionDetails?.tags || [];
        break;
      case FlyoutDataType.Contact:
        this.tags = this.personDetails?.tags || [];
        break;
    }
    this.tags = this.tags.map((item) => ({
      name: item,
      color: this.getLabelColorForTag(item),
    }));
  }

  public removeTagFromContact(tag) {
    if (tag) {
      this.flyoutService.removeTagFromVisitObject(this.getTagRequest(), tag).subscribe(
        () => {
          const tagIndex = this.tags.findIndex((t) => t.name.toLowerCase() === tag.toLowerCase());
          if (tagIndex !== -1) {
            this.tags.splice(tagIndex, 1);
          }
          this.emitVisitDetailsTagUpdate();
          this.snackbarService.success('flyout.tags.tag-removed');
        },
        (error) => {
          this.logger.error('Tag could not be removed from profile: ' + JSON.stringify(error));
          this.snackbarService.error('flyout.tags.tag-remove-failed');
        },
      );
    }
  }

  private getTagRequest(): ManageTagRequest {
    const tagRequest = {
      type: this._openingData.type,
      identifier: this._openingData.identifier,
    } as any;
    if (this.sessionDetails && this.sessionDetails.id) {
      tagRequest.sessionId = this.sessionDetails.id;
    }
    if (this.sessionDetails && this.sessionDetails.uid) {
      tagRequest.uid = this.sessionDetails.uid;
    }
    return tagRequest;
  }

  public addTagInputSubmitted(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;
    if ((value || '').trim()) {
      this.checkDoubleTag(value);
      if (input) {
        input.value = '';
      }
      this.tagsCtrl.setValue(null);
    }
  }

  public addTagSelectSubmitted(event: MatAutocompleteSelectedEvent): void {
    const value = event.option.viewValue;
    if ((value || '').trim()) {
      this.checkDoubleTag(value);
    }
  }

  public checkDoubleTag(value): void {
    const alreadyExists = this.tags && this.tags.some((tag) => tag.name.toLowerCase() === value.trim().toLowerCase());
    if (alreadyExists) {
      return;
    }
    this.addTagToVisitObject(value.trim());
  }

  private addTagToVisitObject(tag: string) {
    if (tag) {
      this.flyoutService.addTagToVisitObject(this.getTagRequest(), tag).subscribe(
        () => {
          this.tags.push({ name: tag, color: this.getLabelColorForTag(tag) });
          this.snackbarService.success('flyout.tags.tag-added');
        },
        (error) => {
          this.logger.error('Tag could not be added to profile: ' + JSON.stringify(error));
          this.snackbarService.error('flyout.tags.tag-add-failed');
        },
      );
    }
  }

  private emitVisitDetailsTagUpdate() {
    this.onVisitDetailsTagsUpdated.emit(this.personDetails.tags);
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.personSubscription, this.sessionSubscription);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  public getLabelColorForTag(label: string): string {
    let color = `118, 94, 185`; // default value
    if (label === 'CurrentCustomer' || label === 'Kund')
      color = `58, 135, 173`; //label-info
    else if (label === 'Competitor' || label === 'Konkurrent')
      color = `185, 74, 72`; // label-important
    else if (label === 'Partner')
      color = `248, 148, 6`; // label-warning
    else if (label === 'Prospect' || label === 'Befintlig Prospect')
      color = `70, 136, 71`; // label-success
    else if (label === 'Supplier' || label === 'Leverantör')
      color = `248, 148, 6`; //label-warning
    else if (label === 'Hot' || label === 'Het')
      color = `185, 74, 72`; // label-important
    else if (label === 'Uninteresting' || label === 'Ointressant') color = `99, 99, 99`; // label-inverse
    return color;
  }
}
